/**
 * Every customer on the books with what accounting keeps for them: the
 * identifier the office types (ID or licence number), the commission %, the
 * group and the rep. Each box saves on its own the moment it changes, so
 * going down the list is one pick per customer and nothing else.
 *
 * The commission % is the customer's own - the View Container screen uses
 * it for every container unless a different one is typed there.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import { api, problemOf } from "./api";

const cell = { fontSize: 14, whiteSpace: "nowrap" };

/** The filter's "has none" value - ids are numbers, so this cannot clash. */
const NONE = "none";

/** Whether a customer passes a group / rep filter. */
export const inList = (value, filter) => {
  if (filter === "") return true;
  if (filter === NONE) return !value;
  return Number(value) === Number(filter);
};

/** Active entries, plus the one already picked even if it was switched off since. */
const offered = (list, current) =>
  list.filter((x) => x.is_active || Number(x.id) === Number(current));

/** What a box's text is sent as: blank is null, a % is a number. */
export const valueFor = (field, text) => {
  const typed = String(text ?? "").trim();
  if (typed === "") return null;
  return field === "commission_percent" ? Number(typed) : typed;
};

/** One typed field of one customer, saved when the box is left. */
function TextBox({ row, field, label, type, width, onSave }) {
  const stored = row[field] === null || row[field] === undefined ? "" : String(row[field]);
  const [value, setValue] = useState(stored);
  const [error, setError] = useState("");

  useEffect(() => setValue(stored), [stored]);

  const save = async () => {
    if (value.trim() === stored) return;
    setError((await onSave({ [field]: valueFor(field, value) })) || "");
  };

  return (
    <TextField
      size="small"
      type={type}
      value={value}
      placeholder="-"
      error={Boolean(error)}
      helperText={error || undefined}
      inputProps={{ "aria-label": `${label} of ${row.code}`, min: 0, step: "any" }}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      onBlur={save}
      sx={{ width }}
    />
  );
}

TextBox.defaultProps = { type: "text", width: 170 };

TextBox.propTypes = {
  row: PropTypes.shape({ code: PropTypes.string }).isRequired,
  field: PropTypes.oneOf(["identifier", "commission_percent"]).isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  width: PropTypes.number,
  onSave: PropTypes.func.isRequired,
};

function Pick({ label, value, list, onChange }) {
  return (
    <TextField
      select
      size="small"
      value={value || ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      SelectProps={{ native: true }}
      inputProps={{ "aria-label": label }}
      sx={{ width: 170 }}
    >
      <option value="">-</option>
      {offered(list, value).map((x) => (
        <option key={x.id} value={x.id}>
          {x.name}
        </option>
      ))}
    </TextField>
  );
}

Pick.defaultProps = { value: null };

Pick.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  list: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

function CustomersTab({ groups, reps, onChanged }) {
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("");
  const [rep, setRep] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/customers")
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => {
        setRows([]);
        setError(problemOf(e, "Could not load the customers"));
      });
  }, []);

  /** Saves one customer's change; returns the problem, or "" when it went through. */
  const save = async (row, change) => {
    try {
      await api.put(`/customers/${row.id}`, change);
      setRows((all) => all.map((r) => (r.id === row.id ? { ...r, ...change } : r)));
      setError("");
      // the group and rep counts on the other tab
      onChanged();
      return "";
    } catch (e) {
      const problem = problemOf(e, "Could not save");
      setError(`${row.code}: ${problem}`);
      return problem;
    }
  };

  const nameOf = (list) => (id) => (list.find((x) => x.id === id) || {}).name || "";
  const groupName = nameOf(groups);
  const repName = nameOf(reps);

  // the group and rep names go into the search too, so "تجار" finds that group's customers
  const searchable = (rows || []).map((r) => ({
    ...r,
    group_name: groupName(r.group_id),
    rep_name: repName(r.rep_id),
  }));
  const shown = filterRows(searchable, query).filter(
    (r) => inList(r.group_id, group) && inList(r.rep_id, rep)
  );
  const pager = usePagination(shown);

  const options = (list) => [
    { value: NONE, label: "None · بدون" },
    ...list.map((x) => ({ value: x.id, label: x.name })),
  ];

  return (
    <SuiBox py={1}>
      <FilterBar
        title="Customers · الزبائن"
        value={query}
        onChange={setQuery}
        count={shown.length}
        total={(rows || []).length}
        placeholder="Search by code, name, phone, identifier..."
      >
        <FilterSelect
          label="Group"
          icon="groups"
          value={group}
          onChange={setGroup}
          options={options(groups)}
        />
        <FilterSelect
          label="Rep"
          icon="badge"
          value={rep}
          onChange={setRep}
          options={options(reps)}
        />
      </FilterBar>

      {error && (
        <SuiBox px={3} pb={1}>
          <SuiTypography variant="button" color="error">
            {error}
          </SuiTypography>
        </SuiBox>
      )}

      {rows === null ? (
        <CargoLoader label="Loading customers" />
      ) : (
        <>
          <SuiBox sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 900 }} aria-label="customers">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Identifier · المعرّف</TableCell>
                  <TableCell>Commission % · العمولة</TableCell>
                  <TableCell>Group · المجموعة</TableCell>
                  <TableCell>Rep · المندوب</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.pageRows.map((r) => (
                  <TableRow key={r.id} sx={{ opacity: r.is_active ? 1 : 0.55 }}>
                    <TableCell sx={{ ...cell, fontWeight: 700 }}>{r.code}</TableCell>
                    <TableCell sx={cell}>{r.name}</TableCell>
                    <TableCell sx={cell}>{r.mobile || "-"}</TableCell>
                    <TableCell sx={cell}>
                      <TextBox
                        row={r}
                        field="identifier"
                        label="Identifier"
                        onSave={(change) => save(r, change)}
                      />
                    </TableCell>
                    <TableCell sx={cell}>
                      <TextBox
                        row={r}
                        field="commission_percent"
                        label="Commission %"
                        type="number"
                        width={100}
                        onSave={(change) => save(r, change)}
                      />
                    </TableCell>
                    <TableCell sx={cell}>
                      <Pick
                        label={`Group of ${r.code}`}
                        value={r.group_id}
                        list={groups}
                        onChange={(id) => save(r, { group_id: id })}
                      />
                    </TableCell>
                    <TableCell sx={cell}>
                      <Pick
                        label={`Rep of ${r.code}`}
                        value={r.rep_id}
                        list={reps}
                        onChange={(id) => save(r, { rep_id: id })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {shown.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <SuiTypography variant="button" color="text">
                        No customers
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SuiBox>
          <Pagination {...pager} />
        </>
      )}
    </SuiBox>
  );
}

CustomersTab.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  reps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default CustomersTab;
